import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Loader2, Save, Construction, FileText, EyeOff, Link2, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { adminApi, api as publicApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

const PAGES = [
  { id: "news", label: "News" },
  { id: "historie", label: "Historie" },
  { id: "events", label: "Events" },
  { id: "projekte", label: "Projekte" },
  { id: "badges", label: "Badges" },
  { id: "sammlung", label: "Sammlung (Möbel + Rares)" },
  { id: "shop", label: "Punkte-Shop" },
  { id: "leaderboard", label: "Leaderboard" },
  { id: "team", label: "Team" },
  { id: "jobs", label: "Jobs" },
  { id: "support", label: "Support" },
];

const CATEGORIES = ["News", "Glitch", "Retro", "Community", "Jubil\u00e4um", "Rare", "Event"];

const QUILL = { toolbar: [["bold", "italic", "underline", "link"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]] };

const SettingsPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => { try { setS(await publicApi.getSettings()); } finally { setLoading(false); } })(); }, []);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const togglePage = (p) => {
    const arr = new Set(s.hidden_pages || []);
    if (arr.has(p)) arr.delete(p); else arr.add(p);
    set("hidden_pages", Array.from(arr));
  };
  const toggleCat = (c) => {
    const arr = new Set(s.hidden_categories || []);
    if (arr.has(c)) arr.delete(c); else arr.add(c);
    set("hidden_categories", Array.from(arr));
  };

  const onSave = async () => {
    setSaving(true);
    try { await a.updateSettings(s); toast({ title: "Einstellungen gespeichert" }); }
    catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (loading || !s) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      {/* Maintenance */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-white"><Construction className="h-4 w-4 text-[#ffb84d]" /> Bauphase / Maintenance</div>
        <div className="mt-3 flex items-center gap-3 rounded-lg bg-[#10141b] p-3 border border-white/10">
          <Switch checked={!!s.maintenance_enabled} onCheckedChange={(v) => set("maintenance_enabled", v)} />
          <span className="text-sm text-zinc-300">{s.maintenance_enabled ? "Bauphase aktiv \u2013 nur Admin sieht die Seite" : "Bauphase deaktiviert"}</span>
        </div>
        <div className="mt-3 space-y-2">
          <Input value={s.maintenance_title || ""} onChange={(e) => set("maintenance_title", e.target.value)} placeholder="Titel" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Textarea value={s.maintenance_message || ""} onChange={(e) => set("maintenance_message", e.target.value)} placeholder="Nachricht" rows={3} className="bg-[#10141b] border-white/10 text-zinc-200" />
        </div>
      </div>

      {/* Hero / Start */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="h-4 w-4 text-[#b39bff]" /> Startseite</div>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
          <Input data-testid="settings-hero-title" value={s.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} placeholder="Hero Titel" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input data-testid="settings-hero-subtitle" value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} placeholder="Hero Untertitel (Slogan)" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input data-testid="settings-hero-badge" value={s.hero_badge_text || ""} onChange={(e) => set("hero_badge_text", e.target.value)} placeholder="Badge-Text über dem Titel (z.B. Inoffizielle Fanseite · Habbo Historie)" className="md:col-span-2 bg-[#10141b] border-white/10 text-zinc-200" />
        </div>
        <div className="mt-3 rounded-lg border border-white/10 bg-[#10141b] p-3">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-400">Slogan-Schriftart</div>
          <div className="mt-1 text-[11px] text-zinc-500">Bestimmt, wie der Untertitel auf der Startseite angezeigt wird.</div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {[
              { id: "handwriting", label: "Handschrift", cls: "handwriting text-lg" },
              { id: "pixel",       label: "Pixel",       cls: "pixel-text text-sm" },
              { id: "serif",       label: "Serif",       cls: "font-serif italic text-lg" },
              { id: "mono",        label: "Mono",        cls: "font-mono text-sm" },
              { id: "sans",        label: "Sans",        cls: "font-sans text-base" },
            ].map((f) => {
              const active = (s.hero_subtitle_font || "handwriting") === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => set("hero_subtitle_font", f.id)}
                  data-testid={`settings-hero-font-${f.id}`}
                  className={`rounded-lg border px-2 py-3 text-center transition-colors ${active ? "border-[#7c4dff] bg-[#7c4dff]/15" : "border-white/10 bg-[#0f141b] hover:border-white/25"}`}
                >
                  <div className={`text-white ${f.cls}`}>Aa</div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-zinc-400">{f.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Seiten-Beschreibungen */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5" data-testid="settings-page-descriptions">
        <div className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="h-4 w-4 text-[#b39bff]" /> Seiten-Beschreibungen</div>
        <p className="mt-1 text-xs text-zinc-500">Ueberschriften und Intro-Texte fuer jede oeffentliche Seite. Felder leer lassen, um den Default-Text aus dem Code zu verwenden.</p>
        <div className="mt-3 space-y-3">
          {[
            { slug: "events", label: "Events" },
            { slug: "news", label: "News" },
            { slug: "rares", label: "Rares" },
            { slug: "sammlung", label: "Sammlung" },
            { slug: "historie", label: "Zeitleiste" },
            { slug: "projekte", label: "Projekte" },
            { slug: "badges", label: "Badges" },
            { slug: "team", label: "Team" },
            { slug: "shop", label: "Shop" },
            { slug: "jobs", label: "Jobs" },
            { slug: "support", label: "Support" },
          ].map(({ slug, label }) => {
            const pd = (s.page_descriptions || {})[slug] || {};
            return (
              <div key={slug} className="rounded-lg border border-white/5 bg-[#10141b] p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-400">{label} <span className="font-mono text-zinc-600">/{slug}</span></div>
                <Input
                  data-testid={`page-desc-eyebrow-${slug}`}
                  value={pd.eyebrow || ""}
                  onChange={(e) => set("page_descriptions", { ...(s.page_descriptions || {}), [slug]: { ...pd, eyebrow: e.target.value } })}
                  placeholder="Kleine Zeile über der Überschrift (Eyebrow, z.B. Werde Teil des Teams)"
                  className="bg-[#0a0d12] border-white/10 text-zinc-200"
                />
                <Input
                  data-testid={`page-desc-title-${slug}`}
                  value={pd.title || ""}
                  onChange={(e) => set("page_descriptions", { ...(s.page_descriptions || {}), [slug]: { ...pd, title: e.target.value } })}
                  placeholder="Titel auf der Seite"
                  className="bg-[#0a0d12] border-white/10 text-zinc-200"
                />
                <Textarea
                  data-testid={`page-desc-intro-${slug}`}
                  value={pd.intro || ""}
                  onChange={(e) => set("page_descriptions", { ...(s.page_descriptions || {}), [slug]: { ...pd, intro: e.target.value } })}
                  rows={2}
                  placeholder="Kurzer Intro-Text unter der Ueberschrift"
                  className="bg-[#0a0d12] border-white/10 text-zinc-200 text-sm"
                />
                <Input
                  data-testid={`page-desc-keywords-${slug}`}
                  value={pd.keywords || ""}
                  onChange={(e) => set("page_descriptions", { ...(s.page_descriptions || {}), [slug]: { ...pd, keywords: e.target.value } })}
                  placeholder="Rotierende Schlagwörter, Komma-getrennt (z.B. Events, Rares, Community)"
                  className="bg-[#0a0d12] border-white/10 text-zinc-200"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer-Links */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5" data-testid="settings-footer-links">
        <div className="text-sm font-semibold text-white flex items-center gap-2"><Link2 className="h-4 w-4 text-[#b39bff]" /> Footer – Sektion "Inhalt"</div>
        <p className="mt-1 text-xs text-zinc-500">Bestimme selbst welche Links unten in der Inhalts-Sektion erscheinen. Pfade beginnen mit "/" (z.B. /events), externe Links mit "https://".</p>
        <div className="mt-3 space-y-2">
          {(s.footer_links || []).map((l, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_3fr_auto] items-center">
              <Input
                data-testid={`footer-link-label-${i}`}
                value={l.label || ""}
                onChange={(e) => {
                  const arr = [...(s.footer_links || [])];
                  arr[i] = { ...arr[i], label: e.target.value };
                  set("footer_links", arr);
                }}
                placeholder="Label (z.B. Zeitleiste)"
                className="bg-[#10141b] border-white/10 text-zinc-200"
              />
              <Input
                data-testid={`footer-link-to-${i}`}
                value={l.to || ""}
                onChange={(e) => {
                  const arr = [...(s.footer_links || [])];
                  arr[i] = { ...arr[i], to: e.target.value };
                  set("footer_links", arr);
                }}
                placeholder="/historie  oder  https://discord.gg/..."
                className="bg-[#10141b] border-white/10 text-zinc-200 font-mono text-xs"
              />
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  onClick={() => {
                    const arr = [...(s.footer_links || [])];
                    if (i > 0) { [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; set("footer_links", arr); }
                  }}
                  disabled={i === 0}
                  className="h-9 w-9 p-0 bg-white/5 border border-white/10 hover:bg-white/10"
                  title="Hoch"
                ><ArrowUp className="h-4 w-4" /></Button>
                <Button
                  type="button"
                  onClick={() => {
                    const arr = [...(s.footer_links || [])];
                    if (i < arr.length - 1) { [arr[i+1], arr[i]] = [arr[i], arr[i+1]]; set("footer_links", arr); }
                  }}
                  disabled={i === (s.footer_links || []).length - 1}
                  className="h-9 w-9 p-0 bg-white/5 border border-white/10 hover:bg-white/10"
                  title="Runter"
                ><ArrowDown className="h-4 w-4" /></Button>
                <Button
                  type="button"
                  data-testid={`footer-link-delete-${i}`}
                  onClick={() => {
                    const arr = [...(s.footer_links || [])];
                    arr.splice(i, 1);
                    set("footer_links", arr);
                  }}
                  className="h-9 w-9 p-0 bg-rose-500/15 border border-rose-500/30 text-rose-300 hover:bg-rose-500/25"
                  title="Loeschen"
                ><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
          <Button
            type="button"
            data-testid="footer-link-add"
            onClick={() => set("footer_links", [...(s.footer_links || []), { label: "", to: "" }])}
            className="btn-ghost h-9 rounded-lg px-3 text-xs"
          ><Plus className="mr-1 h-3.5 w-3.5" /> Neuer Link</Button>
        </div>
      </div>

      {/* Impressum + Datenschutz */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Impressum</div>
        <div className="mt-2 rounded-md border border-white/10 bg-white text-zinc-900">
          <ReactQuill theme="snow" value={s.impressum_html || ""} onChange={(v) => set("impressum_html", v)} modules={QUILL} />
        </div>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Datenschutz</div>
        <div className="mt-2 rounded-md border border-white/10 bg-white text-zinc-900">
          <ReactQuill theme="snow" value={s.datenschutz_html || ""} onChange={(v) => set("datenschutz_html", v)} modules={QUILL} />
        </div>
      </div>

      {/* Badges-Toggles */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white flex items-center gap-2"><EyeOff className="h-4 w-4 text-zinc-400" /> Badge-Seite</div>
        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <Switch
              data-testid="settings-badges-show-manual"
              checked={!!s.badges_show_manual}
              onCheckedChange={(v) => set("badges_show_manual", !!v)}
            />
            Manuell hinzugefügte Badges anzeigen
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <Switch
              data-testid="settings-badges-show-overview"
              checked={!!s.badges_show_overview}
              onCheckedChange={(v) => set("badges_show_overview", !!v)}
            />
            "Besitzer pro Hotel"-Übersicht anzeigen
          </label>
        </div>
        <p className="mt-2 text-[11px] text-zinc-500">Wenn beide aus: nur die HabboAssets-Suche + Frisch hochgeladene Badges + Gamedata-Texte sind sichtbar.</p>
      </div>

      {/* Navbar – konfigurierbar */}
      <NavItemsEditor s={s} set={set} />

      {/* News-Kategorien editierbar */}
      <NewsCategoriesEditor s={s} set={set} />

      {/* Versteckte Seiten + Kategorien */}
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white flex items-center gap-2"><EyeOff className="h-4 w-4 text-zinc-400" /> Sichtbarkeit</div>
        <div className="mt-3 text-xs uppercase tracking-wider text-zinc-500">Seiten ausblenden</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {PAGES.map((p) => {
            const hidden = (s.hidden_pages || []).includes(p.id);
            return (
              <button key={p.id} onClick={() => togglePage(p.id)} className={`rounded-full px-3 py-1 text-xs border ${hidden ? "border-rose-500/50 bg-rose-500/15 text-rose-200 line-through" : "border-white/10 bg-white/5 text-zinc-300"}`}>{p.label}</button>
            );
          })}
        </div>
        <div className="mt-4 text-xs uppercase tracking-wider text-zinc-500">News-Kategorien ausblenden</div>
        <p className="mt-1 text-[10px] text-zinc-600">Versteckt die Kategorie aus dem Filter auf /news. Bestehende Artikel mit dieser Kategorie werden auch in der Übersicht ausgeblendet.</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {(Array.isArray(s.news_categories) && s.news_categories.length > 0 ? s.news_categories : CATEGORIES).map((c) => {
            const hidden = (s.hidden_categories || []).includes(c);
            return (
              <button key={c} data-testid={`hide-cat-${c}`} onClick={() => toggleCat(c)} className={`rounded-full px-3 py-1 text-xs border ${hidden ? "border-rose-500/50 bg-rose-500/15 text-rose-200 line-through" : "border-white/10 bg-white/5 text-zinc-300"}`}>{c}</button>
            );
          })}
        </div>
      </div>

      <div className="sticky bottom-3 z-10 flex justify-end">
        <Button onClick={onSave} disabled={saving} className="btn-primary rounded-lg px-5 py-2 text-sm font-semibold shadow-lg">
          {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Speichern
        </Button>
      </div>
    </div>
  );
};


// ====================== NAV ITEMS EDITOR ======================
const NewsCategoriesEditor = ({ s, set }) => {
  const cats = Array.isArray(s.news_categories) ? s.news_categories : [];
  const update = (idx, value) => set("news_categories", cats.map((c, i) => i === idx ? value : c));
  const remove = (idx) => set("news_categories", cats.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...cats];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    set("news_categories", next);
  };
  const add = () => set("news_categories", [...cats, "Neue Kategorie"]);
  return (
    <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5" data-testid="settings-news-categories">
      <div className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="h-4 w-4 text-[#b39bff]" /> News-Kategorien</div>
      <p className="mt-1 text-[11px] text-zinc-500">Frei umbenennbar und erweiterbar. Werden im News-Filter, Quick-Write und Story-Form benutzt.</p>
      <div className="mt-3 space-y-2">
        {cats.map((c, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              data-testid={`news-cat-${i}`}
              value={c}
              onChange={(e) => update(i, e.target.value)}
              className="h-8 flex-1 bg-[#10141b] border-white/10 text-zinc-200 text-sm"
            />
            <Button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="h-8 w-8 p-0 bg-white/5 border border-white/10"><ArrowUp className="h-3.5 w-3.5" /></Button>
            <Button type="button" onClick={() => move(i, 1)} disabled={i === cats.length - 1} className="h-8 w-8 p-0 bg-white/5 border border-white/10"><ArrowDown className="h-3.5 w-3.5" /></Button>
            <Button type="button" onClick={() => remove(i)} className="h-8 w-8 p-0 bg-rose-500/15 border border-rose-500/30 text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
      <Button type="button" onClick={add} className="btn-ghost mt-3 h-8 rounded-md px-3 text-xs">
        <Plus className="mr-1 h-3.5 w-3.5" /> Neue Kategorie
      </Button>
    </div>
  );
};


// ====================== NAV ITEMS EDITOR ======================
const NavItemsEditor = ({ s, set }) => {
  const items = Array.isArray(s.nav_items) ? s.nav_items : [];
  const update = (idx, patch) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    set("nav_items", next);
  };
  const remove = (idx) => set("nav_items", items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...items];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= next.length) return;
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    set("nav_items", next);
  };
  const addLink = () => set("nav_items", [...items, { type: "link", label: "", to: "", key: "" }]);
  const addDropdown = () => set("nav_items", [...items, { type: "dropdown", label: "Mehr", children: [] }]);
  const addChild = (idx) => {
    const it = items[idx];
    const children = [...(it.children || []), { label: "", to: "" }];
    update(idx, { children });
  };
  const updateChild = (idx, cIdx, patch) => {
    const it = items[idx];
    const children = (it.children || []).map((c, i) => i === cIdx ? { ...c, ...patch } : c);
    update(idx, { children });
  };
  const removeChild = (idx, cIdx) => {
    const it = items[idx];
    const children = (it.children || []).filter((_, i) => i !== cIdx);
    update(idx, { children });
  };
  return (
    <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5" data-testid="settings-nav-items">
      <div className="text-sm font-semibold text-white flex items-center gap-2"><FileText className="h-4 w-4 text-[#b39bff]" /> Navbar – freie Konfiguration</div>
      <p className="mt-1 text-[11px] text-zinc-500">
        Bestimme selbst welche Items oben in der Navigation erscheinen. <b>Leer lassen</b> → Standard-Navbar wird benutzt.
      </p>

      <div className="mt-3 space-y-3">
        {items.length === 0 && (
          <div className="rounded-md border border-white/5 bg-[#10141b] p-3 text-xs italic text-zinc-500">
            Aktuell wird die Standard-Navbar verwendet. Füge ein Item hinzu, um die Navbar zu übernehmen.
          </div>
        )}
        {items.map((it, idx) => (
          <div key={idx} className="rounded-lg border border-white/10 bg-[#10141b] p-3">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={it.type || "link"}
                onChange={(e) => update(idx, { type: e.target.value })}
                className="rounded-md border border-white/10 bg-[#0f141b] px-2 py-1.5 text-xs text-zinc-200"
              >
                <option value="link">Link</option>
                <option value="dropdown">Dropdown</option>
              </select>
              <Input
                data-testid={`nav-item-label-${idx}`}
                value={it.label || ""}
                onChange={(e) => update(idx, { label: e.target.value })}
                placeholder="Label"
                className="h-8 max-w-[140px] bg-[#0f141b] border-white/10 text-zinc-200 text-xs"
              />
              {it.type !== "dropdown" && (
                <Input
                  data-testid={`nav-item-to-${idx}`}
                  value={it.to || ""}
                  onChange={(e) => update(idx, { to: e.target.value })}
                  placeholder="/pfad oder https://..."
                  className="h-8 flex-1 bg-[#0f141b] border-white/10 text-zinc-200 text-xs font-mono"
                />
              )}
              <div className="ml-auto flex gap-1">
                <Button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="h-7 w-7 p-0 bg-white/5 border border-white/10"><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="h-7 w-7 p-0 bg-white/5 border border-white/10"><ArrowDown className="h-3.5 w-3.5" /></Button>
                <Button type="button" onClick={() => remove(idx)} className="h-7 w-7 p-0 bg-rose-500/15 border border-rose-500/30 text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            {it.type === "dropdown" && (
              <div className="mt-3 space-y-2 pl-4 border-l border-white/10">
                {(it.children || []).map((c, cIdx) => (
                  <div key={cIdx} className="flex items-center gap-2">
                    <Input
                      value={c.label || ""}
                      onChange={(e) => updateChild(idx, cIdx, { label: e.target.value })}
                      placeholder="Eintrag-Label"
                      className="h-7 max-w-[140px] bg-[#0f141b] border-white/10 text-zinc-200 text-xs"
                    />
                    <Input
                      value={c.to || ""}
                      onChange={(e) => updateChild(idx, cIdx, { to: e.target.value })}
                      placeholder="/pfad"
                      className="h-7 flex-1 bg-[#0f141b] border-white/10 text-zinc-200 text-xs font-mono"
                    />
                    <Button type="button" onClick={() => removeChild(idx, cIdx)} className="h-7 w-7 p-0 bg-rose-500/15 border border-rose-500/30 text-rose-300"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
                <Button type="button" onClick={() => addChild(idx)} className="btn-ghost h-7 rounded-md px-2 text-[11px]">
                  <Plus className="mr-1 h-3 w-3" /> Eintrag hinzufügen
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <Button type="button" onClick={addLink} className="btn-ghost h-8 rounded-md px-3 text-xs">
          <Plus className="mr-1 h-3.5 w-3.5" /> Neuer Link
        </Button>
        <Button type="button" onClick={addDropdown} className="btn-ghost h-8 rounded-md px-3 text-xs">
          <Plus className="mr-1 h-3.5 w-3.5" /> Neues Dropdown
        </Button>
      </div>
    </div>
  );
};

export default SettingsPanel;
